import React, { useState, useEffect, useCallback } from 'react';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors 
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Save, RotateCcw, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Sortable Item Component
const SortableItem = ({ id, name, translatedName }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-4 flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700 ${
        isDragging ? 'shadow-lg ring-2 ring-primary' : ''
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-white p-1 touch-none"
      >
        <GripVertical className="w-5 h-5" />
      </button>
      <div className="flex-1">
        <p className="text-white font-medium">{translatedName || name}</p>
        {translatedName && translatedName !== name && (
          <p className="text-gray-500 text-sm">{name}</p>
        )}
      </div>
    </div>
  );
};

// Section Component for each sortable category
const SortableSection = ({ title, type, items, setItems, onSave, saving, saved }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Prefix for this section's items
  const prefix = `${type}__`;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    // Extract original IDs by removing the prefix
    const activeOriginalId = String(active.id).replace(prefix, '');
    const overOriginalId = String(over.id).replace(prefix, '');
    
    setItems((prevItems) => {
      const oldIndex = prevItems.findIndex((item) => item.id === activeOriginalId);
      const newIndex = prevItems.findIndex((item) => item.id === overOriginalId);
      if (oldIndex === -1 || newIndex === -1) return prevItems;
      return arrayMove(prevItems, oldIndex, newIndex);
    });
  };

  // Create unique sortable IDs by prefixing with type
  const sortableIds = items.map(item => `${prefix}${item.id}`);

  return (
    <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="flex items-center gap-1 text-green-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              Zapisano
            </span>
          )}
          <button
            onClick={() => onSave(type, items)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Zapisz
          </button>
        </div>
      </div>
      
      {items.length === 0 ? (
        <p className="text-gray-500 text-center py-8">Brak elementów do sortowania</p>
      ) : (
        <DndContext
          id={`dnd-${type}`}
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={sortableIds} 
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {items.map((item) => (
                <SortableItem
                  key={`${prefix}${item.id}`}
                  id={`${prefix}${item.id}`}
                  name={item.name}
                  translatedName={item.translatedName}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

const AdminSortOrder = () => {
  const [disciplines, setDisciplines] = useState([]);
  const [passCategories, setPassCategories] = useState([]);
  const [passes, setPasses] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [error, setError] = useState(null);

  const { token } = useAdminAuth();

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch FitNet data
      const [disciplinesRes, passesRes, classTypesRes] = await Promise.all([
        axios.get(`${API}/api/admin/fitnet/disciplines`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/admin/fitnet/passes`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { groups: [], passes: [] } })),
        axios.get(`${API}/api/admin/fitnet/class-types`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);

      // Fetch existing sort orders
      const [discOrder, catOrder, passOrder, activityOrder] = await Promise.all([
        axios.get(`${API}/api/sort-order/disciplines`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/sort-order/passCategories`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/sort-order/passes`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/sort-order/activityTypes`).catch(() => ({ data: [] }))
      ]);

      // Create order maps
      const discOrderMap = new Map(discOrder.data.map(o => [o.itemId, o.order]));
      const catOrderMap = new Map(catOrder.data.map(o => [o.itemId, o.order]));
      const passOrderMap = new Map(passOrder.data.map(o => [o.itemId, o.order]));
      const activityOrderMap = new Map(activityOrder.data.map(o => [o.itemId, o.order]));

      // Sort and set disciplines
      const sortedDisciplines = (disciplinesRes.data || [])
        .map(d => ({
          id: `disc_${d.id}`,
          name: d.name,
          translatedName: d.translatedName || d.name
        }))
        .sort((a, b) => {
          const orderA = discOrderMap.get(a.id) ?? 999;
          const orderB = discOrderMap.get(b.id) ?? 999;
          return orderA - orderB;
        });
      setDisciplines(sortedDisciplines);

      // Extract pass groups from passes response
      const passGroupsData = passesRes.data?.groups || [];
      
      // Sort and set pass categories
      const sortedCategories = passGroupsData
        .map(c => ({
          id: `cat_${c.id || c.name}`,
          name: c.name,
          translatedName: c.translatedName || c.name
        }))
        .sort((a, b) => {
          const orderA = catOrderMap.get(a.id) ?? 999;
          const orderB = catOrderMap.get(b.id) ?? 999;
          return orderA - orderB;
        });
      setPassCategories(sortedCategories);

      // Extract passes from passes response
      const passesData = passesRes.data?.passes || [];
      
      // Sort and set passes
      const sortedPasses = passesData
        .map(p => ({
          id: `pass_${p.variantId || p.id}`,
          name: p.name,
          translatedName: p.translatedName || p.name
        }))
        .sort((a, b) => {
          const orderA = passOrderMap.get(a.id) ?? 999;
          const orderB = passOrderMap.get(b.id) ?? 999;
          return orderA - orderB;
        });
      setPasses(sortedPasses);

      // Sort and set activity types (class types)
      const classTypesData = classTypesRes.data || [];
      const sortedActivityTypes = classTypesData
        .map(ct => ({
          id: `activity_${ct.code || ct.id}`,
          name: ct.name,
          translatedName: ct.translatedName || ct.name
        }))
        .sort((a, b) => {
          const orderA = activityOrderMap.get(a.id) ?? 999;
          const orderB = activityOrderMap.get(b.id) ?? 999;
          return orderA - orderB;
        });
      setActivityTypes(sortedActivityTypes);

    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Nie udało się pobrać danych');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save sort order
  const handleSave = async (type, items) => {
    try {
      setSaving(prev => ({ ...prev, [type]: true }));
      setSaved(prev => ({ ...prev, [type]: false }));

      const orderedItems = items.map((item, index) => ({
        id: item.id,
        name: item.name,
        order: index
      }));

      await axios.post(
        `${API}/api/sort-order/${type}`,
        { items: orderedItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSaved(prev => ({ ...prev, [type]: true }));
      setTimeout(() => {
        setSaved(prev => ({ ...prev, [type]: false }));
      }, 3000);
    } catch (err) {
      console.error('Failed to save:', err);
      setError('Nie udało się zapisać kolejności');
    } finally {
      setSaving(prev => ({ ...prev, [type]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Kolejność wyświetlania</h1>
          <p className="text-gray-400 mt-1">
            Przeciągnij elementy, aby zmienić ich kolejność na stronie
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Odśwież
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SortableSection
          title="Dyscypliny"
          type="disciplines"
          items={disciplines}
          setItems={setDisciplines}
          onSave={handleSave}
          saving={saving.disciplines}
          saved={saved.disciplines}
        />

        <SortableSection
          title="Typy zajęć"
          type="activityTypes"
          items={activityTypes}
          setItems={setActivityTypes}
          onSave={handleSave}
          saving={saving.activityTypes}
          saved={saved.activityTypes}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SortableSection
          title="Kategorie karnetów"
          type="passCategories"
          items={passCategories}
          setItems={setPassCategories}
          onSave={handleSave}
          saving={saving.passCategories}
          saved={saved.passCategories}
        />

        <SortableSection
          title="Karnety"
          type="passes"
          items={passes}
          setItems={setPasses}
          onSave={handleSave}
          saving={saving.passes}
          saved={saved.passes}
        />
      </div>
    </div>
  );
};

export default AdminSortOrder;
